import React from 'react';
import { useParams } from 'react-router-dom';
import { LoadingOutlined } from '@ant-design/icons';
import { PERMISSIONS } from 'constants/permissions';
import { Button, Col, HasAccess, Row } from 'components';
import { HasAccess as AccessControl } from 'components/common/accessControl';
import { usePurchaseOrderActions } from './usePurchaseOrderActions';
import { PurchaseOrderItems } from './items';
import styles from './styles.module.scss';
import { usePurchaseOrderItemsListQuery } from 'api';
import { ALLOWED_STATUSES_FOR_SUPPLIER_OFFER_CREATION } from 'models/PurchaseOrder';
import { AddSupplierOfferModal } from 'pages/admin/supplierOffers/addSupplierOffer';
import { SUPPLIER_OFFER_SOURCE } from 'models/SupplierOffer';

export const PurchaseOrderGeneral = React.memo(() => {
  const { id } = useParams() as { id: string };
  const {
    pdfGenerated,
    isGeneratingPdf,
    isSendingOrder,
    handleGeneratePdf,
    handleSendOrder,
    purchaseOrder,
    onSupplierOfferModalStateChange,
    supplierOfferModalState,
  } = usePurchaseOrderActions({ purchaseOrderId: id });
  const isInWarehouse = purchaseOrder?.status === 'IN_WAREHOUSE';
  const { data = {} } = usePurchaseOrderItemsListQuery(
    {
      purchaseOrderId: id,
    },
    {
      skip: !id,
    }
  );
  return (
    <>
      <AccessControl role={PERMISSIONS.purchaseOrders.purchaseOrder.read}>
        <Row className={styles.generalContainer}>
          <Col span={24} style={{ marginTop: 20 }}>
            <HasAccess role={PERMISSIONS.items.item.read}>
              <PurchaseOrderItems purchaseOrderId={id} />
            </HasAccess>
          </Col>

          {/* Action Buttons Section */}
          <Col span={24} className={styles.actionsSection}>
            <Row justify="end" gutter={[16, 16]}>
              <Col>
                <HasAccess role={PERMISSIONS.purchaseOrders.purchaseOrder.write}>
                  <Button
                    type="primary"
                    size="large"
                    ghost
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    href={purchaseOrder.attachment?.files?.ATTACHMENTS}
                    disabled={!purchaseOrder.pdfGenerated}
                  >
                    Download PDF
                  </Button>
                </HasAccess>
              </Col>
              <Col>
                <HasAccess role={PERMISSIONS.purchaseOrders.purchaseOrder.write}>
                  <Button
                    block
                    size="large"
                    onClick={onSupplierOfferModalStateChange}
                    type="primary"
                    ghost
                    disabled={!ALLOWED_STATUSES_FOR_SUPPLIER_OFFER_CREATION.includes(purchaseOrder?.status)}
                  >
                    Create Supplier Offer
                  </Button>
                </HasAccess>
              </Col>
              <Col>
                <HasAccess role={PERMISSIONS.purchaseOrders.purchaseOrder.write}>
                  <Button
                    type="primary"
                    size="large"
                    icon={isGeneratingPdf ? <LoadingOutlined /> : undefined}
                    loading={isGeneratingPdf}
                    onClick={handleGeneratePdf}
                    disabled={isInWarehouse}
                  >
                    {isGeneratingPdf ? 'Generating PDF...' : 'Generate PDF'}
                  </Button>
                </HasAccess>
              </Col>
              <Col>
                <HasAccess role={PERMISSIONS.purchaseOrders.purchaseOrder.write}>
                  <Button
                    type="primary"
                    size="large"
                    icon={isSendingOrder ? <LoadingOutlined /> : undefined}
                    loading={isSendingOrder}
                    onClick={handleSendOrder}
                    disabled={!pdfGenerated || isInWarehouse}
                  >
                    {isSendingOrder ? 'Sending Order...' : 'Send Order'}
                  </Button>
                </HasAccess>
              </Col>
            </Row>
          </Col>
        </Row>
      </AccessControl>
      <AddSupplierOfferModal
        onModalStateChange={onSupplierOfferModalStateChange}
        modalState={supplierOfferModalState}
        values={data?.content}
        type={'PURCHASE_ORDER'}
        source={SUPPLIER_OFFER_SOURCE.PO_DETAILS}
        entityId={id}
      />
    </>
  );
});
